import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSharedSelector } from "../../../../store/SharedProvider";
import {
  CleaningConfiguration,
  CleaningConfigurationLevel,
  DEFAULT_MIN_OBSTACLE_HEIGHT,
  getCleaningModeOptionText,
  getCleaningToggleOptionText,
  IndependentConfigurationLegacy2601,
  MinObstacleHeightOption,
} from "../../../../interfaces/robot/cleaning-configurations/global-cleaning-configuration";
import {
  DEFAULT_ENVIRONMENT_POLLUTION_TYPE,
  EnvironmentPollutionType,
  getGlobalEnvironmentPollutionList,
} from "../../../../interfaces/robot/cleaning-configurations/environment-pollution";
import {
  selectDistanceUnit,
  selectIsAdvancedModeEnabled,
} from "../../../../store/app-configurations/app-configurations-selectors";
import {
  DEFAULT_ENVIRONMENT_HAZARD_TYPE,
  EnvironmentHazardType,
  getGlobalEnvironmentHazardTypes,
} from "../../../../interfaces/robot/cleaning-configurations/environment-hazard";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { getEnumSelectOptions } from "../../../../utils/enums";
import { CustomTuneFiltersIcon, InheritIcon } from "../../../../utils/icons/icons";
import OptionSwitchInput, { PlaceholderOptions } from "../../../Inputs/OptionSwitchInput";
import CustomEnvironmentPollution from "./CustomEnvironmentPollution";
import SpatialPropertySlider from "../SpatialPropertySlider";
import {
  DEFAULT_SPATIAL_PROPERTY_TYPE,
  SpatialPropertyType,
} from "../../../../interfaces/robot/cleaning-configurations/spatialProperty";
import Divider from "@mui/material/Divider";
import ReadOnlyProtectedForm from "../../../Inputs/ReadOnlyProtectedForm";
import { DistanceUnit } from "../../../../enums/configuration/units";
import { MapRulesConfiguration } from "../../../../interfaces/robot/cleaning-configurations/map-rules-configuration";
import InheritableDrivingSpeedSlider from "./InheritableDrivingSpeedSlider";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import CustomAlert from "../../../Feedback/CustomAlert";
import RobotVersionControlledWrapper from "../../../misc/RobotVersionControlledWrapper";
import {
  BEEPER_DEFAULT_VALUE,
  WARNING_LIGHT_DEFAULT_VALUE,
} from "../../../Room/ZonePlanner/SelectedControls/SelectedZoneFormRuleZoneDetails";

const EnvironmentCleaningConfiguration: React.FC<{
  configurationLevel: CleaningConfigurationLevel;
  hideDivider?: boolean;
  allowSpatialPropertyConfig?: boolean;
  globalCleaningConfiguration?: CleaningConfiguration;
  roomCleaningConfiguration?: CleaningConfiguration;
  zoneCleaningConfiguration?: CleaningConfiguration;
  globalMapRulesConfiguration?: MapRulesConfiguration;
  roomMapRulesConfiguration?: MapRulesConfiguration;
  roomHasMapFiles?: boolean;
  onConfigurationChange: (payload: Partial<CleaningConfiguration> | Partial<MapRulesConfiguration>) => void;
}> = ({
  hideDivider,
  configurationLevel,
  allowSpatialPropertyConfig,
  zoneCleaningConfiguration,
  globalCleaningConfiguration,
  roomCleaningConfiguration,
  globalMapRulesConfiguration,
  roomMapRulesConfiguration,
  roomHasMapFiles,
  onConfigurationChange,
}) => {
  const { t } = useTranslation();

  const isAdvancedModeEnabled = useSharedSelector((state) => selectIsAdvancedModeEnabled(state));
  const distanceUnit = useSharedSelector((state) => selectDistanceUnit(state));

  const [isCustomEnvironmentPollutionOpen, setIsCustomEnvironmentPollutionOpen] = useState(false);

  const minObstacleHeightChangeHandler = (newValue: string | number) => {
    const value = typeof newValue === "string" ? parseFloat(newValue) : (newValue as number);
    onConfigurationChange({ minObstacleHeightM: value });
  };

  const customizeEnvironmentPollutionClickHandler = () => {
    setIsCustomEnvironmentPollutionOpen(true);
  };

  const closeCustomEnvironmentPollutionHandler = () => {
    setIsCustomEnvironmentPollutionOpen(false);
  };

  const drivingSpeedChangeHandler = (value: number) => {
    onConfigurationChange({ drivingSpeed: value });
  };

  const independentLegacyChangeHandler = (changes: Partial<IndependentConfigurationLegacy2601>) => {
    onConfigurationChange({
      independentLegacy2601: {
        beeper: zoneCleaningConfiguration?.independentLegacy2601?.beeper ?? null,
        warningLight: zoneCleaningConfiguration?.independentLegacy2601?.warningLight ?? null,
        minObstacleHeightM: zoneCleaningConfiguration?.independentLegacy2601?.minObstacleHeightM ?? null,
        ...changes,
      },
    });
  };

  const isBlockStorageAllowed = roomHasMapFiles && isAdvancedModeEnabled;

  return (
    <React.Fragment>
      <ReadOnlyProtectedForm>
        <Stack spacing={2} divider={hideDivider ? undefined : <Divider flexItem />}>
          <Stack>
            <OptionSwitchInput
              label={t("configuration-cleaning-environmentPollution")}
              helpText={t("configuration-cleaning-environmentPollution-info")}
              options={getGlobalEnvironmentPollutionList.map((ep) => ({
                label: t(ep),
                value: ep,
              }))}
              value={
                zoneCleaningConfiguration?.environmentPollution ??
                roomCleaningConfiguration?.environmentPollution ??
                globalCleaningConfiguration?.environmentPollution
              }
              loading={
                (configurationLevel === CleaningConfigurationLevel.Zone && zoneCleaningConfiguration == null) ||
                (configurationLevel === CleaningConfigurationLevel.Room && roomCleaningConfiguration == null) ||
                globalCleaningConfiguration == null
              }
              onChange={(v: string) => onConfigurationChange({ environmentPollution: v as EnvironmentPollutionType })}
              subButton={
                zoneCleaningConfiguration != null
                  ? {
                      label:
                        zoneCleaningConfiguration.environmentPollution == null
                          ? t("button-override-default-configs")
                          : getCleaningModeOptionText(
                              null,
                              configurationLevel,
                              roomCleaningConfiguration?.environmentPollution,
                              globalCleaningConfiguration?.environmentPollution ?? undefined,
                            ),
                      startIcon:
                        zoneCleaningConfiguration.environmentPollution == null ? (
                          <CustomTuneFiltersIcon />
                        ) : (
                          <InheritIcon />
                        ),
                      onClick: () =>
                        onConfigurationChange({
                          environmentPollution:
                            zoneCleaningConfiguration.environmentPollution == null
                              ? (roomCleaningConfiguration?.environmentPollution ??
                                globalCleaningConfiguration?.environmentPollution ??
                                DEFAULT_ENVIRONMENT_POLLUTION_TYPE)
                              : null,
                        }),
                    }
                  : roomCleaningConfiguration != null
                    ? {
                        label:
                          roomCleaningConfiguration.environmentPollution == null
                            ? t("button-override-default-configs")
                            : getCleaningModeOptionText(
                                null,
                                configurationLevel,
                                roomCleaningConfiguration?.environmentPollution,
                                globalCleaningConfiguration?.environmentPollution ?? undefined,
                              ),
                        startIcon:
                          roomCleaningConfiguration.environmentPollution == null ? (
                            <CustomTuneFiltersIcon />
                          ) : (
                            <InheritIcon />
                          ),
                        onClick: () =>
                          onConfigurationChange({
                            environmentPollution:
                              roomCleaningConfiguration.environmentPollution == null
                                ? (globalCleaningConfiguration?.environmentPollution ??
                                  DEFAULT_ENVIRONMENT_POLLUTION_TYPE)
                                : null,
                          }),
                      }
                    : undefined
              }
              buttonOverrideText={
                configurationLevel === CleaningConfigurationLevel.Zone &&
                zoneCleaningConfiguration?.environmentPollution == null
                  ? getCleaningModeOptionText(
                      null,
                      configurationLevel,
                      roomCleaningConfiguration?.environmentPollution,
                      globalCleaningConfiguration?.environmentPollution ?? undefined,
                    )
                  : configurationLevel === CleaningConfigurationLevel.Room &&
                      roomCleaningConfiguration?.environmentPollution == null
                    ? getCleaningModeOptionText(
                        null,
                        configurationLevel,
                        roomCleaningConfiguration?.environmentPollution,
                        globalCleaningConfiguration?.environmentPollution ?? undefined,
                      )
                    : undefined
              }
            />

            {globalCleaningConfiguration != null && configurationLevel === CleaningConfigurationLevel.Global && (
              <Button
                sx={{ width: "max-content" }}
                startIcon={<CustomTuneFiltersIcon />}
                onClick={customizeEnvironmentPollutionClickHandler}
              >
                {t("customize-custom-configuration")}
              </Button>
            )}
          </Stack>

          {(roomCleaningConfiguration != null || (zoneCleaningConfiguration != null && allowSpatialPropertyConfig)) && (
            <Stack maxWidth="100%" spacing={0.5}>
              <InputLabel>{t("configuration-cleaning-spatialProperty")}</InputLabel>
              {configurationLevel === CleaningConfigurationLevel.Zone &&
              zoneCleaningConfiguration?.spatialProperty == null ? (
                <Typography>
                  {getCleaningModeOptionText(
                    null,
                    configurationLevel,
                    roomCleaningConfiguration?.spatialProperty,
                    globalCleaningConfiguration?.spatialProperty ?? undefined,
                  )}
                </Typography>
              ) : (
                <SpatialPropertySlider
                  hideLabel
                  value={
                    zoneCleaningConfiguration?.spatialProperty ??
                    roomCleaningConfiguration?.spatialProperty ??
                    SpatialPropertyType.Static
                  }
                  isBlockStorageAllowed={isBlockStorageAllowed}
                  onChange={(v: SpatialPropertyType) => onConfigurationChange({ spatialProperty: v })}
                />
              )}

              {isAdvancedModeEnabled &&
                (zoneCleaningConfiguration?.spatialProperty === SpatialPropertyType.BlockStorage ||
                  (zoneCleaningConfiguration?.spatialProperty == null &&
                    roomCleaningConfiguration?.spatialProperty === SpatialPropertyType.BlockStorage)) && (
                  <CustomAlert severity="info" header={t("warning-label-quite-dynamic-used")} />
                )}

              {configurationLevel === CleaningConfigurationLevel.Zone && (
                <Button
                  onClick={() =>
                    onConfigurationChange({
                      spatialProperty:
                        zoneCleaningConfiguration?.spatialProperty == null
                          ? (roomCleaningConfiguration?.spatialProperty ??
                            globalCleaningConfiguration?.spatialProperty ??
                            DEFAULT_SPATIAL_PROPERTY_TYPE)
                          : null,
                    })
                  }
                  variant="text"
                  size="small"
                  sx={{ width: "max-content", maxWidth: "100%", overflow: "hidden" }}
                  startIcon={
                    zoneCleaningConfiguration?.spatialProperty == null &&
                    configurationLevel === CleaningConfigurationLevel.Zone ? (
                      <CustomTuneFiltersIcon />
                    ) : (
                      <InheritIcon />
                    )
                  }
                >
                  {zoneCleaningConfiguration?.spatialProperty == null &&
                  configurationLevel === CleaningConfigurationLevel.Zone
                    ? t("button-override-default-configs")
                    : getCleaningModeOptionText(
                        null,
                        configurationLevel,
                        roomCleaningConfiguration?.spatialProperty,
                        globalCleaningConfiguration?.spatialProperty ?? undefined,
                      )}
                </Button>
              )}
              <FormHelperText>{t("configuration-cleaning-spatialProperty-info")}</FormHelperText>
            </Stack>
          )}

          {configurationLevel !== CleaningConfigurationLevel.Zone && isAdvancedModeEnabled && (
            <OptionSwitchInput
              label={t("configuration-cleaning-environmentHazard")}
              helpText={t("environment-hazard-description")}
              options={getGlobalEnvironmentHazardTypes.map((eh) => ({
                label: t(eh),
                value: eh,
                tooltip: t(`${eh}-description`),
              }))}
              value={roomCleaningConfiguration?.environmentHazard ?? globalCleaningConfiguration?.environmentHazard}
              loading={
                (configurationLevel === CleaningConfigurationLevel.Room && roomCleaningConfiguration == null) ||
                globalCleaningConfiguration == null
              }
              onChange={(v: string) => onConfigurationChange({ environmentHazard: v as EnvironmentHazardType })}
              subButton={
                roomCleaningConfiguration != null
                  ? {
                      label:
                        roomCleaningConfiguration.environmentHazard == null
                          ? t("button-override-default-configs")
                          : getCleaningModeOptionText(
                              null,
                              configurationLevel,
                              roomCleaningConfiguration?.environmentHazard,
                              globalCleaningConfiguration?.environmentHazard ?? undefined,
                            ),
                      startIcon:
                        roomCleaningConfiguration.environmentHazard == null ? (
                          <CustomTuneFiltersIcon />
                        ) : (
                          <InheritIcon />
                        ),
                      onClick: () =>
                        onConfigurationChange({
                          environmentHazard:
                            roomCleaningConfiguration.environmentHazard == null
                              ? (globalCleaningConfiguration?.environmentHazard ?? DEFAULT_ENVIRONMENT_HAZARD_TYPE)
                              : null,
                        }),
                    }
                  : undefined
              }
              buttonOverrideText={
                configurationLevel === CleaningConfigurationLevel.Room &&
                roomCleaningConfiguration?.environmentHazard == null
                  ? getCleaningModeOptionText(
                      null,
                      configurationLevel,
                      roomCleaningConfiguration?.environmentHazard,
                      globalCleaningConfiguration?.environmentHazard ?? undefined,
                    )
                  : undefined
              }
            />
          )}

          {configurationLevel !== CleaningConfigurationLevel.Zone && globalMapRulesConfiguration != null && (
            <RobotVersionControlledWrapper minVersion="2026.1.0">
              <InheritableDrivingSpeedSlider
                value={roomMapRulesConfiguration?.drivingSpeed ?? globalMapRulesConfiguration.drivingSpeed ?? 100}
                label="configuration-cleaning-EP-drivingSpeed"
                onChange={drivingSpeedChangeHandler}
                subButton={
                  roomMapRulesConfiguration != null
                    ? {
                        label:
                          roomMapRulesConfiguration.drivingSpeed == null
                            ? t("button-override-default-configs")
                            : getCleaningModeOptionText(
                                null,
                                configurationLevel,
                                roomMapRulesConfiguration?.drivingSpeed,
                                globalMapRulesConfiguration?.drivingSpeed ?? undefined,
                              ),
                        startIcon:
                          roomMapRulesConfiguration.drivingSpeed == null ? <CustomTuneFiltersIcon /> : <InheritIcon />,
                        onClick: () =>
                          onConfigurationChange({
                            drivingSpeed:
                              roomMapRulesConfiguration.drivingSpeed == null
                                ? (globalMapRulesConfiguration?.drivingSpeed ?? 100)
                                : null,
                          }),
                      }
                    : undefined
                }
                overrideText={
                  configurationLevel === CleaningConfigurationLevel.Room &&
                  roomMapRulesConfiguration?.drivingSpeed == null
                    ? getCleaningModeOptionText(
                        null,
                        configurationLevel,
                        roomMapRulesConfiguration?.drivingSpeed,
                        globalMapRulesConfiguration?.drivingSpeed ?? undefined,
                      )
                    : undefined
                }
              />
            </RobotVersionControlledWrapper>
          )}

          {configurationLevel !== CleaningConfigurationLevel.Zone && (
            <OptionSwitchInput
              label={t("cleaning-configuration-min-obstacle-height")}
              options={getEnumSelectOptions(MinObstacleHeightOption).map((option) => ({
                label: t(`cleaning-configuration-min-obstacle-height-${option.name}`),
                value: option.value,
                tooltip: t(
                  option.name + "-description" + (distanceUnit === DistanceUnit.METRIC ? "-metric" : "-imperial"),
                ),
              }))}
              value={roomMapRulesConfiguration?.minObstacleHeightM ?? globalMapRulesConfiguration?.minObstacleHeightM}
              loading={
                (configurationLevel === CleaningConfigurationLevel.Room && roomMapRulesConfiguration == null) ||
                globalMapRulesConfiguration == null
              }
              onChange={minObstacleHeightChangeHandler}
              subButton={
                roomMapRulesConfiguration != null
                  ? {
                      label:
                        roomMapRulesConfiguration.minObstacleHeightM == null
                          ? t("button-override-default-configs")
                          : getCleaningModeOptionText(
                              null,
                              configurationLevel,
                              roomMapRulesConfiguration?.minObstacleHeightM,
                              globalMapRulesConfiguration?.minObstacleHeightM ?? undefined,
                            ),
                      startIcon:
                        roomMapRulesConfiguration.minObstacleHeightM == null ? (
                          <CustomTuneFiltersIcon />
                        ) : (
                          <InheritIcon />
                        ),
                      onClick: () =>
                        onConfigurationChange({
                          minObstacleHeightM:
                            roomMapRulesConfiguration.minObstacleHeightM == null
                              ? (globalMapRulesConfiguration?.minObstacleHeightM ?? DEFAULT_MIN_OBSTACLE_HEIGHT)
                              : null,
                        }),
                    }
                  : undefined
              }
              buttonOverrideText={
                configurationLevel === CleaningConfigurationLevel.Room &&
                roomMapRulesConfiguration?.minObstacleHeightM == null
                  ? getCleaningModeOptionText(
                      null,
                      configurationLevel,
                      roomMapRulesConfiguration?.minObstacleHeightM,
                      globalMapRulesConfiguration?.minObstacleHeightM ?? undefined,
                    )
                  : undefined
              }
            />
          )}
          {configurationLevel === CleaningConfigurationLevel.Zone && (
            <RobotVersionControlledWrapper maxVersion="2026.1.0">
              <Stack spacing={2}>
                <OptionSwitchInput
                  label={t("cleaning-configuration-beeper")}
                  options={[
                    { label: t("cleaning-configuration-switch-on"), value: PlaceholderOptions.True },
                    { label: t("cleaning-configuration-switch-off"), value: PlaceholderOptions.False },
                  ]}
                  value={
                    zoneCleaningConfiguration?.independentLegacy2601?.beeper != null
                      ? zoneCleaningConfiguration.independentLegacy2601.beeper
                        ? PlaceholderOptions.True
                        : PlaceholderOptions.False
                      : roomCleaningConfiguration?.independentLegacy2601?.beeper != null
                        ? roomCleaningConfiguration.independentLegacy2601.beeper
                          ? PlaceholderOptions.True
                          : PlaceholderOptions.False
                        : (globalMapRulesConfiguration?.beeper ?? BEEPER_DEFAULT_VALUE)
                          ? PlaceholderOptions.True
                          : PlaceholderOptions.False
                  }
                  loading={globalMapRulesConfiguration == null}
                  onChange={(v: string) => independentLegacyChangeHandler({ beeper: v === PlaceholderOptions.True })}
                  subButton={{
                    label:
                      zoneCleaningConfiguration?.independentLegacy2601?.beeper == null
                        ? t("button-override-default-configs")
                        : getCleaningToggleOptionText(
                            null,
                            configurationLevel,
                            roomCleaningConfiguration?.independentLegacy2601?.beeper ?? null,
                            globalMapRulesConfiguration?.beeper ?? undefined,
                          ),
                    startIcon:
                      zoneCleaningConfiguration?.independentLegacy2601?.beeper == null ? (
                        <CustomTuneFiltersIcon />
                      ) : (
                        <InheritIcon />
                      ),
                    onClick: () =>
                      independentLegacyChangeHandler({
                        beeper:
                          zoneCleaningConfiguration?.independentLegacy2601?.beeper == null
                            ? (roomCleaningConfiguration?.independentLegacy2601?.beeper ??
                              globalMapRulesConfiguration?.beeper ??
                              BEEPER_DEFAULT_VALUE)
                            : null,
                      }),
                  }}
                  buttonOverrideText={
                    zoneCleaningConfiguration?.independentLegacy2601?.beeper == null
                      ? getCleaningToggleOptionText(
                          null,
                          configurationLevel,
                          roomCleaningConfiguration?.independentLegacy2601?.beeper ?? null,
                          globalMapRulesConfiguration?.beeper ?? undefined,
                        )
                      : undefined
                  }
                />

                <OptionSwitchInput
                  label={t("cleaning-configuration-warning-light")}
                  options={[
                    { label: t("cleaning-configuration-switch-on"), value: PlaceholderOptions.True },
                    { label: t("cleaning-configuration-switch-off"), value: PlaceholderOptions.False },
                  ]}
                  value={
                    zoneCleaningConfiguration?.independentLegacy2601?.warningLight != null
                      ? zoneCleaningConfiguration.independentLegacy2601.warningLight
                        ? PlaceholderOptions.True
                        : PlaceholderOptions.False
                      : roomCleaningConfiguration?.independentLegacy2601?.warningLight != null
                        ? roomCleaningConfiguration.independentLegacy2601.warningLight
                          ? PlaceholderOptions.True
                          : PlaceholderOptions.False
                        : (globalMapRulesConfiguration?.warningLight ?? WARNING_LIGHT_DEFAULT_VALUE)
                          ? PlaceholderOptions.True
                          : PlaceholderOptions.False
                  }
                  loading={globalMapRulesConfiguration == null}
                  onChange={(v: string) =>
                    independentLegacyChangeHandler({ warningLight: v === PlaceholderOptions.True })
                  }
                  subButton={{
                    label:
                      zoneCleaningConfiguration?.independentLegacy2601?.warningLight == null
                        ? t("button-override-default-configs")
                        : getCleaningToggleOptionText(
                            null,
                            configurationLevel,
                            roomCleaningConfiguration?.independentLegacy2601?.warningLight ?? null,
                            globalMapRulesConfiguration?.warningLight ?? undefined,
                          ),
                    startIcon:
                      zoneCleaningConfiguration?.independentLegacy2601?.warningLight == null ? (
                        <CustomTuneFiltersIcon />
                      ) : (
                        <InheritIcon />
                      ),
                    onClick: () =>
                      independentLegacyChangeHandler({
                        warningLight:
                          zoneCleaningConfiguration?.independentLegacy2601?.warningLight == null
                            ? (roomCleaningConfiguration?.independentLegacy2601?.warningLight ??
                              globalMapRulesConfiguration?.warningLight ??
                              WARNING_LIGHT_DEFAULT_VALUE)
                            : null,
                      }),
                  }}
                  buttonOverrideText={
                    zoneCleaningConfiguration?.independentLegacy2601?.warningLight == null
                      ? getCleaningToggleOptionText(
                          null,
                          configurationLevel,
                          roomCleaningConfiguration?.independentLegacy2601?.warningLight ?? null,
                          globalMapRulesConfiguration?.warningLight ?? undefined,
                        )
                      : undefined
                  }
                />

                <OptionSwitchInput
                  label={t("cleaning-configuration-min-obstacle-height")}
                  options={getEnumSelectOptions(MinObstacleHeightOption).map((option) => ({
                    label: t(`cleaning-configuration-min-obstacle-height-${option.name}`),
                    value: option.value,
                    tooltip: t(
                      option.name + "-description" + (distanceUnit === DistanceUnit.METRIC ? "-metric" : "-imperial"),
                    ),
                  }))}
                  value={
                    zoneCleaningConfiguration?.independentLegacy2601?.minObstacleHeightM ??
                    roomCleaningConfiguration?.independentLegacy2601?.minObstacleHeightM ??
                    globalMapRulesConfiguration?.minObstacleHeightM
                  }
                  loading={globalMapRulesConfiguration == null}
                  onChange={(v: string | number) =>
                    independentLegacyChangeHandler({
                      minObstacleHeightM: typeof v === "string" ? parseFloat(v) : v,
                    })
                  }
                  subButton={{
                    label:
                      zoneCleaningConfiguration?.independentLegacy2601?.minObstacleHeightM == null
                        ? t("button-override-default-configs")
                        : getCleaningModeOptionText(
                            null,
                            configurationLevel,
                            roomCleaningConfiguration?.independentLegacy2601?.minObstacleHeightM,
                            globalMapRulesConfiguration?.minObstacleHeightM ?? undefined,
                          ),
                    startIcon:
                      zoneCleaningConfiguration?.independentLegacy2601?.minObstacleHeightM == null ? (
                        <CustomTuneFiltersIcon />
                      ) : (
                        <InheritIcon />
                      ),
                    onClick: () =>
                      independentLegacyChangeHandler({
                        minObstacleHeightM:
                          zoneCleaningConfiguration?.independentLegacy2601?.minObstacleHeightM == null
                            ? (roomCleaningConfiguration?.independentLegacy2601?.minObstacleHeightM ??
                              globalMapRulesConfiguration?.minObstacleHeightM ??
                              DEFAULT_MIN_OBSTACLE_HEIGHT)
                            : null,
                      }),
                  }}
                  buttonOverrideText={
                    zoneCleaningConfiguration?.independentLegacy2601?.minObstacleHeightM == null
                      ? getCleaningModeOptionText(
                          null,
                          configurationLevel,
                          roomCleaningConfiguration?.independentLegacy2601?.minObstacleHeightM,
                          globalMapRulesConfiguration?.minObstacleHeightM ?? undefined,
                        )
                      : undefined
                  }
                />
              </Stack>
            </RobotVersionControlledWrapper>
          )}
        </Stack>
      </ReadOnlyProtectedForm>
      <CustomEnvironmentPollution
        isOpen={isCustomEnvironmentPollutionOpen}
        onClose={closeCustomEnvironmentPollutionHandler}
      />
    </React.Fragment>
  );
};

export default EnvironmentCleaningConfiguration;
